import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { formatCurrency } from '../../../components/charts/d3/format';
import { colors, radii, spacing, typography } from '../../../core/theme/tokens';
import type { ImportMonthSection, ImportWsTransaction } from '../../../types/import';

type Props = {
  monthSections: ImportMonthSection[];
  transactionCount: number;
  /** When true, show a compact “still arriving…” caption. */
  loading?: boolean;
};

function rowKey(tx: ImportWsTransaction, index: number): string {
  return `${tx.date.slice(0, 10)}|${tx.merchant}|${tx.amount}|${tx.direction}|${index}`;
}

export function ImportTransactionList({
  monthSections,
  transactionCount,
  loading,
}: Props) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  if (transactionCount === 0 && !loading) {
    return null;
  }

  return (
    <View style={styles.wrap}>
      <View style={styles.header}>
        <Text style={styles.title}>Imported transactions</Text>
        <Text style={styles.count}>
          {loading && transactionCount === 0
            ? 'Loading…'
            : `${transactionCount} · newest first`}
        </Text>
      </View>

      {monthSections.map((month) => (
        <View key={month.monthKey} style={styles.monthBlock}>
          <View style={styles.monthHeader}>
            <Text style={styles.monthLabel}>{month.label}</Text>
            <Text style={styles.monthMeta}>
              {month.count} · {formatCurrency(month.debitTotal)}
            </Text>
          </View>

          {month.days.map((section) => (
            <View key={section.day} style={styles.section}>
              <Text style={styles.dayLabel}>{section.label}</Text>
              {section.items.map((tx, index) => {
                const key = `${month.monthKey}|${rowKey(tx, index)}`;
                const open = expandedKey === key;
                const signed =
                  tx.direction === 'credit'
                    ? formatCurrency(tx.amount)
                    : `-${formatCurrency(tx.amount)}`;
                return (
                  <Pressable
                    key={key}
                    onPress={() => setExpandedKey(open ? null : key)}
                    accessibilityRole="button"
                    accessibilityState={{ expanded: open }}
                    accessibilityLabel={`${tx.merchant}, ${signed}, ${tx.categoryName}`}
                    style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
                  >
                    <View style={styles.rowMain}>
                      <View style={styles.meta}>
                        <Text style={styles.merchant} numberOfLines={open ? 3 : 1}>
                          {tx.merchant}
                        </Text>
                        <Text style={styles.category} numberOfLines={1}>
                          {tx.categoryName}
                          {tx.tab === 'wealth' ? ' · wealth' : ''}
                        </Text>
                      </View>
                      <Text
                        style={[
                          styles.amount,
                          tx.direction === 'credit' && styles.amountCredit,
                        ]}
                      >
                        {signed}
                      </Text>
                    </View>
                    {open ? (
                      <View style={styles.detail}>
                        <Text style={styles.detailLine}>
                          {section.label} · {tx.direction} · {tx.kind}
                        </Text>
                        {tx.description ? (
                          <Text style={styles.detailLine}>{tx.description}</Text>
                        ) : null}
                        {typeof tx.confidence === 'number' ? (
                          <Text style={styles.detailLine}>
                            Confidence {Math.round(tx.confidence * 100)}%
                          </Text>
                        ) : null}
                      </View>
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.lg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  title: {
    ...typography.headline,
    color: colors.text,
    fontSize: 16,
  },
  count: {
    ...typography.caption,
    color: colors.textMuted,
  },
  monthBlock: {
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  monthHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingBottom: 4,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  monthLabel: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
    fontSize: 11,
    fontWeight: '700',
  },
  monthMeta: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  section: {
    gap: 2,
    marginBottom: spacing.xs,
  },
  dayLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    fontSize: 10,
    marginBottom: 4,
    marginTop: spacing.xs,
  },
  row: {
    borderRadius: radii.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    gap: 6,
  },
  rowPressed: {
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  rowMain: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  meta: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  merchant: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  category: {
    ...typography.caption,
    color: colors.textMuted,
  },
  amount: {
    ...typography.body,
    color: colors.text,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  amountCredit: {
    color: colors.accent,
  },
  detail: {
    gap: 2,
    paddingLeft: 2,
  },
  detailLine: {
    ...typography.caption,
    color: colors.textMuted,
  },
});
